import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Toaster, toast } from "sonner";
import { Radio, TowerControl } from "lucide-react";
import "@/public-app.css";

import { getDispatches, getIncidents, getResources, updateDispatch } from "@/lib/api";
import { useLiveStream } from "@/lib/useLiveStream";
import FieldLogin from "@/components/field-app/FieldLogin";
import FieldNav from "@/components/field-app/FieldNav";
import DutyFeed from "@/components/field-app/DutyFeed";
import FieldReport from "@/components/field-app/FieldReport";
import FieldStatus from "@/components/field-app/FieldStatus";

const KEY = "go_field_officer";

export default function FieldApp() {
  const [officer, setOfficer] = React.useState(() => { try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; } });
  const [tab, setTab] = React.useState("duty");
  const [nationalAlert, setNationalAlert] = React.useState(null);
  const [updatingId, setUpdatingId] = React.useState(null);
  const qc = useQueryClient();
  const enabled = !!officer;

  const { data: dispatchData, isLoading: dLoading } = useQuery({ queryKey: ["dispatch", officer?.id], queryFn: () => getDispatches(officer.id), enabled, refetchInterval: 30000 });
  const { data: incData } = useQuery({ queryKey: ["incidents"], queryFn: getIncidents, enabled, refetchInterval: 30000 });
  const { data: resData } = useQuery({ queryKey: ["resources"], queryFn: getResources, enabled, refetchInterval: 30000 });

  const dispatches = dispatchData?.items || [];
  const incidents = incData?.items || [];
  const myUnit = (resData?.units || []).find((u) => u.id === officer?.id) || officer;

  useLiveStream(enabled ? "field" : null, (evt) => {
    if (evt.type === "national_alert" && evt.payload?.alert) setNationalAlert(evt.payload.alert);
    else if (evt.type === "dispatch") {
      qc.invalidateQueries({ queryKey: ["dispatch", officer?.id] });
      if (evt.payload?.action === "new" && evt.payload.dispatch?.unit_id === officer?.id) {
        toast.warning(`New dispatch: ${evt.payload.dispatch.title}`, { description: evt.payload.dispatch.location });
      }
    } else if (evt.type === "incident") qc.invalidateQueries({ queryKey: ["incidents"] });
    else if (evt.type === "resource") qc.invalidateQueries({ queryKey: ["resources"] });
    else if (evt.type === "message" && evt.payload?.target === "field") toast.info(evt.payload.sender_name || "Command", { description: evt.payload.body });
  });

  const login = (u) => { const o = { id: u.id, callsign: u.callsign, service: u.service, station: u.station }; localStorage.setItem(KEY, JSON.stringify(o)); setOfficer(o); toast.success(`On duty as ${u.callsign}`); };
  const logout = () => { localStorage.removeItem(KEY); setOfficer(null); };

  const onUpdate = async (id, status) => {
    setUpdatingId(id);
    try { await updateDispatch(id, { status }); qc.invalidateQueries({ queryKey: ["dispatch", officer?.id] }); qc.invalidateQueries({ queryKey: ["resources"] }); toast.success(status === "cleared" ? "Incident cleared" : `Marked ${status.replace("_", " ")}`); }
    catch { toast.error("Update failed"); } finally { setUpdatingId(null); }
  };

  if (!officer) return (
    <div className="go-app field-app min-h-screen bg-[#070E20]">
      <div className="mx-auto min-h-screen max-w-md"><FieldLogin onSelect={login} /></div>
      <Toaster position="top-center" richColors />
    </div>
  );

  const activeDispatchCount = dispatches.filter((d) => d.status === "pending").length;

  return (
    <div className="go-app field-app min-h-screen overflow-x-hidden bg-[#070E20]">
      <div className="relative mx-auto flex min-h-screen max-w-md flex-col bg-[#070E20]">
        {/* header */}
        <div className="flex items-center gap-2.5 border-b border-white/10 px-5 py-3 pt-[max(env(safe-area-inset-top),12px)]">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#1E4DB7]"><Radio size={18} color="#FFC726" strokeWidth={2.3} /></span>
          <div className="min-w-0 flex-1"><div className="font-head text-sm font-extrabold leading-tight text-white">{officer.callsign}</div><div className="truncate text-[11px] text-[#67799A]">{officer.service} · {myUnit?.status || "on duty"}</div></div>
          <span className="go-blip inline-block h-2 w-2 rounded-full bg-[#22E0A1]" />
        </div>

        <main className="relative flex-1 overflow-hidden">
          {tab === "duty" && <DutyFeed dispatches={dispatches} incidents={incidents} loading={dLoading} onUpdate={onUpdate} updatingId={updatingId} />}
          {tab === "report" && <FieldReport officer={officer} onDone={() => setTab("duty")} />}
          {tab === "status" && <FieldStatus officer={officer} unit={myUnit} onChanged={() => qc.invalidateQueries({ queryKey: ["resources"] })} onLogout={logout} />}
        </main>

        <FieldNav active={tab} onChange={setTab} dutyCount={activeDispatchCount} />

        {nationalAlert && (
          <div className="fixed inset-0 z-[2000] flex flex-col items-center justify-center bg-[#EF4444] px-8 text-center text-white" data-testid="field-national-alert">
            <div className="go-dial-pulse mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-white/20"><TowerControl size={52} strokeWidth={2.2} /></div>
            <div className="mb-2 text-sm font-bold uppercase tracking-[0.2em]">National Alert</div>
            <h1 className="font-head text-3xl font-extrabold leading-tight">{nationalAlert.title}</h1>
            <p className="mt-4 max-w-sm text-base leading-relaxed text-white/90">{nationalAlert.body}</p>
            <div className="mt-3 text-sm text-white/70">{nationalAlert.issued_by}</div>
            <button onClick={() => setNationalAlert(null)} className="mt-10 rounded-full bg-white px-10 py-3.5 text-base font-bold text-[#EF4444] active:scale-95" data-testid="field-alert-ack">Acknowledge</button>
          </div>
        )}
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}
